import type { SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import type { RunStatus } from "@shared/types/runs";
import type {
  OverviewAlert,
  OverviewAlertKind,
  OverviewImports,
  OverviewRuns,
  RunStatusCounts,
} from "@shared/types/overview";
import { runStatusPresentation } from "@/components/runs/runFormatting";
import { formatNextFire } from "@/lib/timezones";
import { instantHoverTitle, isUtcZone } from "@/lib/time/displayTime";

/**
 * How the Overview's numbers read (Session 10, B1). Pure, so every sentence the cards say is
 * pinned by a test without rendering a card.
 *
 * Two rules from the endpoint hold here too: a count that could not be read is `null` and reads
 * as a dash or a sentence, never as `0`; and colour is never the only signal, so every segment
 * of the runs bar is also a word in its label and in the counts beside it.
 */

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

/**
 * The runs bar's segments, in the order they are drawn: outcomes first, then what is still in
 * flight. Token classes only (`scripts/check-palette.mjs`).
 */
export const RUN_BAR_SEGMENTS: ReadonlyArray<{ status: RunStatus; className: string }> = [
  { status: "succeeded", className: "bg-success" },
  { status: "partially_succeeded", className: "bg-warning" },
  { status: "failed", className: "bg-destructive" },
  { status: "cancelled", className: "bg-muted-foreground" },
  { status: "running", className: "bg-info" },
  { status: "queued", className: "bg-info-subtle" },
];

/** The status words the bar and its counts use: the Runs screen's own labels. */
export function runStatusWord(status: RunStatus): string {
  return runStatusPresentation(status).label;
}

/** Each non-empty segment with its share of the whole, for the bar's widths. */
export function runBarSegments(
  counts: RunStatusCounts,
): Array<{ status: RunStatus; className: string; count: number; percent: number }> {
  if (counts.total <= 0) return [];
  return RUN_BAR_SEGMENTS.filter((segment) => counts[segment.status] > 0).map((segment) => ({
    ...segment,
    count: counts[segment.status],
    percent: (counts[segment.status] / counts.total) * 100,
  }));
}

/**
 * The bar's accessible name: what a sighted reader gets from the colours, in words.
 * `12 runs in the last 24 hours: 10 Succeeded, 2 Failed.`
 */
export function runBarLabel(counts: RunStatusCounts): string {
  if (counts.total <= 0) return "No runs in the last 24 hours.";
  const parts = RUN_BAR_SEGMENTS.filter((segment) => counts[segment.status] > 0).map(
    (segment) => `${counts[segment.status].toLocaleString()} ${runStatusWord(segment.status)}`,
  );
  return `${plural(counts.total, "run")} in the last 24 hours: ${parts.join(", ")}.`;
}

/**
 * #1211: every status a window's runs ended in (or are still in), in words, in the bar's order,
 * so the parts add up to the total the sentence names and every grey cancelled segment the strip
 * draws is also said: `10 succeeded, 1 failed, 1 cancelled`. Empty when there are no runs.
 */
export function runCountsWords(counts: RunStatusCounts): string {
  return RUN_BAR_SEGMENTS.filter((segment) => counts[segment.status] > 0)
    .map(
      (segment) =>
        `${counts[segment.status].toLocaleString()} ${runStatusWord(segment.status).toLowerCase()}`,
    )
    .join(", ");
}

/** A window's sentence after its label: `12 runs. 10 succeeded, 1 failed, 1 cancelled.` */
function windowSentence(when: string, counts: RunStatusCounts): string {
  if (counts.total <= 0) return `${when}: no runs.`;
  const words = runCountsWords(counts);
  const total = plural(counts.total, "run");
  return words ? `${when}: ${total}. ${words}.` : `${when}: ${total}.`;
}

/** `Last 7 days: 12 runs. 10 succeeded, 1 failed, 1 cancelled.` */
export function sevenDayLine(runs: OverviewRuns): string {
  return windowSentence("Last 7 days", runs.last7d);
}

/** A strip column's window: "Last 24 hours", "1 day ago", "N days ago". */
function dayAgeLabel(age: number): string {
  if (age === 0) return "Last 24 hours";
  if (age === 1) return "1 day ago";
  return `${age} days ago`;
}

/**
 * One column of the Runs card's 7-day strip, in words (round 4): its window, then what the 7-day
 * line says of a week. `index` counts from the oldest (0) to the last 24 hours (`days - 1`):
 * `6 days ago: 8 runs. 7 succeeded, 1 failed.`, `Last 24 hours: no runs.` The windows are rolling 24 hours,
 * so "2 days ago" is the 24 hours that ended 48 hours ago, not a calendar day.
 */
export function stripDayLabel(counts: RunStatusCounts, index: number, days: number): string {
  return windowSentence(dayAgeLabel(days - 1 - index), counts);
}

/** Each day's height in the strip: its share of the busiest day (0 to 100). */
export function stripHeights(daily: readonly RunStatusCounts[]): number[] {
  const busiest = Math.max(0, ...daily.map((day) => day.total));
  return daily.map((day) => (busiest > 0 ? (day.total / busiest) * 100 : 0));
}

/** A row count, or a dash when nobody reported it (never `0`). */
export function formatRowCount(value: number | null): string {
  return value === null ? "—" : value.toLocaleString();
}

/**
 * Whether the imports card has anything to report. Both sums are null when no counted run in
 * the window reported tables (P1: "No imports in the last 24 hours", not 0).
 */
export function hasImportTotals(imports: OverviewImports): boolean {
  return imports.rowsExtracted !== null || imports.rowsLoaded !== null;
}

/**
 * #1240: where the rows that were extracted and not loaded come from, by the status of the runs
 * that hold them, in the order a reader acts on: still importing, then cancelled, failed, partly
 * failed, and last a run that succeeded with a load that reported fewer rows.
 */
const NOT_LOADED_ORDER: ReadonlyArray<{ status: RunStatus; one: string; many: string }> = [
  { status: "queued", one: "queued run, not loaded yet", many: "queued runs, not loaded yet" },
  { status: "running", one: "run still importing", many: "runs still importing" },
  { status: "cancelled", one: "cancelled run", many: "cancelled runs" },
  { status: "failed", one: "failed run", many: "failed runs" },
  { status: "partially_succeeded", one: "partly failed run", many: "partly failed runs" },
  {
    status: "succeeded",
    one: "succeeded run whose load reported fewer rows",
    many: "succeeded runs whose load reported fewer rows",
  },
];

/** "1,000 from 1 cancelled run" for each part of the gap that holds rows, in reading order. */
function notLoadedParts(imports: OverviewImports): string[] {
  const parts = NOT_LOADED_ORDER.flatMap(({ status, one, many }) => {
    const entry = imports.notLoaded?.[status];
    if (!entry || entry.rows <= 0) return [];
    return [`${entry.rows.toLocaleString()} from ${plural(entry.runs, one, many)}`];
  });
  const unreported = imports.loadNotReported;
  if (unreported && unreported.rows > 0) {
    parts.push(
      `${unreported.rows.toLocaleString()} from ${plural(unreported.runs, "succeeded run whose load reported no row count", "succeeded runs whose load reported no row count")}`,
    );
  }
  return parts;
}

/** The sum of the server's parts, or null when they cannot be checked against the sums. */
function partsTotal(imports: OverviewImports): number | null {
  if (!imports.notLoaded || !imports.loadedMore) return null;
  const notLoaded = Object.values(imports.notLoaded).reduce((sum, entry) => sum + entry.rows, 0);
  return notLoaded + (imports.loadNotReported?.rows ?? 0) - imports.loadedMore.rows;
}

/**
 * #1240: why Extracted and Loaded differ, or null when they do not (or nobody reported a sum).
 * `Extracted is 1,000 more than Loaded. Not loaded: 1,000 from 1 cancelled run.` The parts are
 * named only when they add up to the gap the sentence states (the server's `notLoaded`,
 * `loadNotReported` and `loadedMore`); otherwise, as with an older server, the sentence says only
 * where to look. With no load figure at all, Loaded is a dash, so nothing is compared with it.
 */
export function importGapNote(imports: OverviewImports): string | null {
  const extracted = imports.rowsExtracted;
  if (extracted === null) return null;
  const parts = notLoadedParts(imports);
  const notLoaded = parts.length > 0 ? ` Not loaded: ${parts.join(", ")}.` : "";
  if (imports.rowsLoaded === null) {
    if (extracted === 0 && parts.length === 0) return null;
    return `No load reported a row count in the last ${plural(imports.windowHours, "hour")}.${notLoaded}`;
  }
  const loaded = imports.rowsLoaded;
  const gap = extracted - loaded;
  const more = imports.loadedMore;
  const reconciles = partsTotal(imports) === gap;
  if (gap === 0 && (!reconciles || parts.length === 0)) return null;
  const head =
    gap > 0
      ? `Extracted is ${gap.toLocaleString()} more than Loaded.`
      : gap < 0
        ? `Loaded is ${(-gap).toLocaleString()} more than Extracted.`
        : "Extracted and Loaded are equal overall.";
  if (!reconciles) {
    return gap < 0
      ? `${head} A table's load can report rows its extract did not count. Each run's tables are on the Runs screen.`
      : `${head} The Runs screen shows which runs did not load every row.`;
  }
  const loadedMore =
    more && more.rows > 0
      ? ` Loaded more than extracted: ${more.rows.toLocaleString()} from ${plural(more.runs, "run whose load reported more rows than its extract", "runs whose load reported more rows than their extract")}.`
      : "";
  return `${head}${notLoaded}${loadedMore}`;
}

/** The imports card's footnotes, in the order they are shown. */
export function importFootnotes(imports: OverviewImports): string[] {
  const notes: string[] = [];
  const gap = importGapNote(imports);
  if (gap) notes.push(gap);
  if (imports.runsWithoutTotals > 0) {
    notes.push(
      `${plural(imports.runsWithoutTotals, "run")} in the window reported no tables: transform-only runs, or imports that have not extracted yet.`,
    );
  }
  if (imports.capped) {
    notes.push(
      `Counted the newest ${imports.runsCounted.toLocaleString()} runs. The last 24 hours held more.`,
    );
  }
  return notes;
}

/**
 * The alerts card's empty sentence. "No open failures." when the week HAD failures and every
 * one of them was fixed by a later success (the endpoint clears those); otherwise "No failures
 * in the last 7 days." A week the role cannot read counts as no failures.
 */
export function alertsEmptySentence(runs: OverviewRuns | null): string {
  const week = runs?.last7d;
  if (week && week.failed + week.partially_succeeded > 0) return "No open failures.";
  return "No failures in the last 7 days.";
}

/** The alert kinds that are failures; the rest (a partial import, failed tests) are warnings. */
const FAILURE_KINDS: ReadonlySet<OverviewAlertKind> = new Set<OverviewAlertKind>([
  "run_failed",
  "transform_failed",
]);

/** The alert's bar: a failure is an error, a partial import or failed tests a warning. */
export function alertIntent(alert: OverviewAlert): "error" | "warning" {
  return FAILURE_KINDS.has(alert.kind) ? "error" : "warning";
}

/**
 * The alert's body: the runner's step sentence when it sent one, else the dbt cause's label,
 * else nothing. Never composed here (SEC-17): only the server's words.
 */
export function alertMessage(alert: OverviewAlert): string | null {
  if (alert.detail) return alert.detail;
  if (alert.cause) return alert.cause.sentence || alert.cause.label;
  return null;
}

/** A stable key: the same run can carry a run alert and a tests alert (P1, request 6). */
export function alertKey(alert: OverviewAlert): string {
  return `${alert.kind}:${alert.runId}`;
}

/**
 * A next fire in the schedule's zone, `Fri Sep 25, 6:00 AM PDT`, from `lib/timezones`
 * `formatNextFire`: the second line of the card's hover (`nextFireHover`), labelled "In the
 * schedule's zone", never its only reading. The zone is the SCHEDULE's: "6:00 AM" is what the person who made it chose.
 *
 * A zone the runtime cannot format is not labelled with it: the shared helper then reads the
 * instant in Pacific, named (#1078), never the reader's clock under the schedule's zone name.
 */
export function nextFireText(iso: string, timezone: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return formatNextFire(at, timezone);
}

/**
 * The hover of a next fire on the Overview card (#1078 review): first the exact instant in the
 * reader's display zone, named, as every hover over an instant reads (`instantHoverTitle`); then,
 * on a second line, the schedule's own words when its zone is not the reader's. A schedule in UTC
 * never adds that line: the next run is an instant, and an instant a person reads is never UTC
 * (owner rule on #1078; the UTC exception covers a schedule's own rule, not its next run).
 */
export function nextFireHover(
  iso: string,
  scheduleZone: string,
  viewerZone: string,
): string | undefined {
  const exact = instantHoverTitle(iso, viewerZone);
  if (!exact) return undefined;
  if (isUtcZone(scheduleZone) || scheduleZone === viewerZone) return exact;
  return `${exact}\nIn the schedule's zone: ${nextFireText(iso, scheduleZone)}`;
}

/** The badge colour of a test tally: any failure is danger, all passing is success. */
export function testsBadgeColor(failed: number): SkyBadgeColor {
  return failed > 0 ? "danger" : "success";
}
